import { cbSafe } from "../lib/colourBlind";
import React, { useEffect, useState } from "react";
import { theme, card, floatingSheet, btnPrimary, inputStyle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { COUNTRIES } from "../data/countriesData";
import { PortalLink } from "../components/PortalLink";
import { US_STATES } from "../data/usStates";

// Sites — the offices, plants, data centres and remote hubs of this
// organisation. One organisation stays one management system; sites say where
// it operates so a single certificate can cover several locations and every
// country's laws are picked up. A site's country is added to Operating
// Locations on the organisation profile automatically.

export interface Site {
  id: string;
  name: string;
  kind: SiteKind;
  country: string;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  region: string | null;
  postcode: string | null;
  headcount: number | null;
  legalEntityName: string | null;
  isLegalEstablishment: boolean;
  isCentralFunction: boolean;
  inScope: boolean;
  notes: string | null;
  // The site this one reports to (organisational only, nothing inherited) and
  // the departments operating here — both set on the site's page.
  parentSiteId: string | null;
  parent: { id: string; name: string } | null;
  departments: Array<{ id: string; name: string; color: string }>;
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
  controlCount?: number;
  evidenceCount?: number;
  riskCount?: number;
  dependentCount?: number;
  addendumCount?: number;
}

/** Sites in tree order: each site followed by the sites reporting to it, indented by `depth`. */
export function siteTree<T extends { id: string; parentSiteId: string | null }>(sites: T[]): Array<T & { depth: number }> {
  const byParent = new Map<string | null, T[]>();
  const ids = new Set(sites.map((s) => s.id));
  for (const s of sites) {
    // A parent outside the list (never, but defensively) renders at the top level.
    const key = s.parentSiteId && ids.has(s.parentSiteId) ? s.parentSiteId : null;
    (byParent.get(key) ?? byParent.set(key, []).get(key)!).push(s);
  }
  const out: Array<T & { depth: number }> = [];
  const seen = new Set<string>();
  const walk = (parent: string | null, depth: number) => {
    for (const s of byParent.get(parent) ?? []) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      out.push({ ...s, depth });
      walk(s.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/** Ids of a site and everything reporting to it, however deep — the sites it may not report to. */
export function siteDescendants(sites: Array<{ id: string; parentSiteId: string | null }>, id: string): Set<string> {
  const out = new Set<string>([id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const s of sites) if (s.parentSiteId && out.has(s.parentSiteId) && !out.has(s.id)) { out.add(s.id); grew = true; }
  }
  return out;
}

export type SiteKind = "hq" | "branch" | "data_centre" | "remote" | "other";

export const SITE_KIND_LABELS: Record<SiteKind, string> = {
  hq:          "Head office",
  branch:      "Branch / office",
  data_centre: "Data centre",
  remote:      "Remote hub",
  other:       "Other",
};

interface SitesResponse {
  sites: Site[];
  summary: { countries: string[]; totalHeadcount: number; inScopeCount: number; centralFunctionSiteId: string | null };
}

export function siteLabel(s: Pick<Site, "name" | "country">) {
  return `${s.name} · ${s.country}`;
}

const fieldStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: 4 };
const labelStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, fontWeight: 600 };
const hintStyle: React.CSSProperties = { fontSize: 11, color: theme.color.subtle, lineHeight: 1.4 };

function Toggle({ checked, onChange, label, hint }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint: string }) {
  return (
    <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3 }} />
      <span>
        <span style={{ fontSize: 13, color: theme.color.text, display: "block" }}>{label}</span>
        <span style={hintStyle}>{hint}</span>
      </span>
    </label>
  );
}

// ── Create / edit modal ───────────────────────────────────────────────────────
// Also opened from the site detail page (SiteDetail.tsx).

export function SiteModal({ initial, hasCentralFunction, sites, onClose, onSaved }: {
  initial?: Site;
  hasCentralFunction: boolean;
  /** Every site of the organisation, for the "reports to" choice. */
  sites: Array<Pick<Site, "id" | "name" | "parentSiteId">>;
  onClose: () => void;
  onSaved: (s: Site) => void;
}) {
  const [name, setName]                 = useState(initial?.name ?? "");
  const [kind, setKind]                 = useState<SiteKind>(initial?.kind ?? (hasCentralFunction ? "branch" : "hq"));
  const [country, setCountry]           = useState(initial?.country ?? "");
  const [addressLine1, setAddressLine1] = useState(initial?.addressLine1 ?? "");
  const [addressLine2, setAddressLine2] = useState(initial?.addressLine2 ?? "");
  const [city, setCity]                 = useState(initial?.city ?? "");
  const [region, setRegion]             = useState(initial?.region ?? "");
  const [postcode, setPostcode]         = useState(initial?.postcode ?? "");
  const [headcount, setHeadcount]       = useState(initial?.headcount != null ? String(initial.headcount) : "");
  const [legalEntityName, setLegalEntityName] = useState(initial?.legalEntityName ?? "");
  const [isLegalEstablishment, setIsLegalEstablishment] = useState(initial?.isLegalEstablishment ?? true);
  const [isCentralFunction, setIsCentralFunction] = useState(initial?.isCentralFunction ?? !hasCentralFunction);
  const [inScope, setInScope]           = useState(initial?.inScope ?? true);
  const [notes, setNotes]               = useState(initial?.notes ?? "");
  const [parentSiteId, setParentSiteId] = useState(initial?.parentSiteId ?? "");
  const [saving, setSaving]             = useState(false);
  // A site cannot report to itself or to anything that reports to it.
  const excluded = initial ? siteDescendants(sites, initial.id) : new Set<string>();
  const parentOptions = sites.filter((x) => !excluded.has(x.id));
  const [error, setError]               = useState("");

  const text = (v: string) => (v.trim() ? v.trim() : null);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (!country) { setError("Choose the country this site is in."); return; }
    const n = headcount.trim() === "" ? null : Number(headcount);
    if (n !== null && (!Number.isInteger(n) || n < 0)) { setError("Headcount must be a whole number."); return; }
    if (n !== null && n > 1_000_000) { setError("Headcount must be 1,000,000 or fewer."); return; }
    setSaving(true);
    try {
      const body = {
        name: name.trim(), kind, country,
        addressLine1: text(addressLine1), addressLine2: text(addressLine2), city: text(city), region: text(region), postcode: text(postcode),
        headcount: n, legalEntityName: text(legalEntityName),
        isLegalEstablishment, isCentralFunction, inScope, notes: text(notes),
        parentSiteId: parentSiteId || null,
      };
      const result = initial
        ? await api.patch<{ site: Site }>(`/sites/${initial.id}`, body)
        : await api.post<{ site: Site }>("/sites", body);
      onSaved(result.site);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, "Save failed"));
    } finally {
      setSaving(false);
    }
  }

  const overlay: React.CSSProperties = {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
    display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16,
  };

  return (
    <div style={overlay} onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ ...floatingSheet, padding: 20, width: "100%", maxWidth: 560, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, paddingBottom: 10, borderBottom: "1px solid var(--c-line)" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: theme.color.heading }}>{initial ? "Edit site" : "New site"}</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: theme.color.muted, fontSize: 20, lineHeight: 1 }}>×</button>
        </div>
        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Name</label>
              <input style={inputStyle} value={name} autoFocus required maxLength={80} onChange={(e) => setName(e.target.value)} placeholder="e.g. London office" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Type</label>
              <select style={inputStyle} value={kind} onChange={(e) => setKind(e.target.value as SiteKind)}>
                {(Object.keys(SITE_KIND_LABELS) as SiteKind[]).map((k) => <option key={k} value={k}>{SITE_KIND_LABELS[k]}</option>)}
              </select>
            </div>
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Country</label>
            <select style={inputStyle} value={country} required onChange={(e) => setCountry(e.target.value)}>
              <option value="">Choose a country…</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <div style={hintStyle}>Added to Operating Locations on the organisation profile; its regulations apply from then on.</div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div style={fieldStyle}>
              <label style={labelStyle}>Address line 1</label>
              <input style={inputStyle} value={addressLine1} maxLength={120} onChange={(e) => setAddressLine1(e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Address line 2</label>
              <input style={inputStyle} value={addressLine2} maxLength={120} onChange={(e) => setAddressLine2(e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>City</label>
              <input style={inputStyle} value={city} maxLength={80} onChange={(e) => setCity(e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>{country === "United States" ? "State" : "Region / state"}</label>
              {country === "United States" ? (
                // A US site's state brings that state's laws into scope, so it is picked, not typed.
                <select style={inputStyle} value={US_STATES.includes(region as never) ? region : ""} onChange={(e) => setRegion(e.target.value)}>
                  <option value="">Choose a state…</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : (
                <input style={inputStyle} value={region} maxLength={80} onChange={(e) => setRegion(e.target.value)} />
              )}
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Postcode</label>
              <input style={inputStyle} value={postcode} maxLength={20} onChange={(e) => setPostcode(e.target.value)} />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Headcount</label>
              <input style={inputStyle} value={headcount} inputMode="numeric" onChange={(e) => setHeadcount(e.target.value)} placeholder="People based here" />
            </div>
          </div>

          {parentOptions.length > 0 && (
            <div style={fieldStyle}>
              <label style={labelStyle}>Reports to (optional)</label>
              <select style={inputStyle} value={parentSiteId} onChange={(e) => setParentSiteId(e.target.value)}>
                <option value="">— No parent site —</option>
                {parentOptions.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
              <div style={hintStyle}>The office this site reports to, for the site list and the auditor's picture of the organisation. Nothing is inherited through it.</div>
            </div>
          )}

          <div style={fieldStyle}>
            <label style={labelStyle}>Legal entity (optional)</label>
            <input style={inputStyle} value={legalEntityName} maxLength={120} onChange={(e) => setLegalEntityName(e.target.value)} placeholder="Only when this site is run by a different legal entity" />
            <div style={hintStyle}>For a group run as one management system. Subsidiaries that certify separately belong in their own organisation instead.</div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 12, borderRadius: 3, background: theme.color.panelAlt }}>
            <Toggle checked={isCentralFunction} onChange={setIsCentralFunction} label="Central function"
              hint="The site from which the management system is planned, controlled and managed (IAF MD 1). Usually the head office." />
            <Toggle checked={inScope} onChange={setInScope} label="In certification scope"
              hint="Untick for a location that operates outside the management system. Its country still counts as an operating location." />
            <Toggle checked={isLegalEstablishment} onChange={setIsLegalEstablishment} label="Legal establishment"
              hint="A registered presence in this country. Declared on the data protection threshold profile, where it settles whether a local representative is needed." />
          </div>

          <div style={fieldStyle}>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} value={notes} maxLength={2000} onChange={(e) => setNotes(e.target.value)} />
          </div>

          {error && <div style={{ fontSize: 12, color: theme.color.dangerText }}>{error}</div>}
          <div style={hintStyle}>
            The people, departments and responsibilities at this site, and the controls, risks, evidence and tasks tagged to it, are managed on the site's page
            {initial ? <> — <PortalLink to={`/sites/${initial.id}`} style={{ color: theme.color.accentText }}>open {initial.name}</PortalLink></> : " once it is saved"}.
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 4 }}>
            <button type="button" onClick={onClose} style={{ ...inputStyle, width: "auto", padding: "8px 16px", cursor: "pointer" }}>Cancel</button>
            <button type="submit" disabled={saving || !name.trim()} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1 }}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ ...card, padding: "12px 16px", minWidth: 140, flex: "1 1 140px" }}>
      <div style={{ fontSize: 11, color: theme.color.subtle, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: theme.color.heading, marginTop: 2 }}>{value}</div>
    </div>
  );
}

export function SitesPage() {
  const { user } = useCurrentUser();
  // Mirrors the server: every write on this page needs org:admin.
  const isAdmin = !!user?.permissions?.includes("org:admin");

  const [data, setData]             = useState<SitesResponse | null>(null);
  const [loading, setLoading]       = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing]       = useState<Site | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [deleting, setDeleting]     = useState(false);
  const [delError, setDelError]     = useState("");

  function load() {
    setLoading(true);
    api.get<SitesResponse>("/sites").then(setData).catch(() => {}).finally(() => setLoading(false));
  }
  useEffect(() => { load(); }, []);

  async function handleDelete(id: string) {
    setDeleting(true); setDelError("");
    try {
      await api.del(`/sites/${id}`);
      setConfirmDel(null);
      load();
    } catch (err) {
      setDelError(apiErrorMessage(err, "Delete failed"));
    } finally {
      setDeleting(false);
    }
  }

  const sites = data?.sites ?? [];
  const summary = data?.summary;
  const hasCentralFunction = sites.some((s) => s.isCentralFunction);

  const cell: React.CSSProperties = {
    padding: "12px 16px", borderBottom: `1px solid ${theme.color.border}`, fontSize: 13, color: theme.color.text, verticalAlign: "top",
  };
  const th: React.CSSProperties = { ...cell, borderBottom: `1px solid ${theme.color.line}`, fontWeight: 600, color: theme.color.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em" };
  const colCount = isAdmin ? 6 : 5;

  function address(s: Site) {
    return [s.addressLine1, s.addressLine2, s.city, s.region, s.postcode].filter(Boolean).join(", ");
  }

  return (
    <div className="page-pad" style={{ fontFamily: theme.font }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, gap: 16 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: theme.color.heading, letterSpacing: "-0.3px" }}>Sites &amp; locations</h1>
          <p style={{ margin: 0, fontSize: 13, color: theme.color.subtle, maxWidth: 640 }}>
            The offices, plants, data centres and remote hubs this organisation runs under one management system.
            A site's country joins your Operating Locations automatically, so the laws that apply there are picked up.
            Sites never change what a member can see or do.
          </p>
        </div>
        {isAdmin && (
          <button onClick={() => setShowCreate(true)} style={{ ...btnPrimary, flexShrink: 0 }}>+ New site</button>
        )}
      </div>

      {summary && sites.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
          <Stat label="Sites" value={sites.length} />
          <Stat label="In scope" value={summary.inScopeCount} />
          <Stat label="Countries" value={summary.countries.length} />
          <Stat label="Headcount" value={summary.totalHeadcount || "—"} />
        </div>
      )}

      {sites.length > 1 && !hasCentralFunction && (
        <div style={{ ...card, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: theme.color.warningText, borderColor: theme.color.warningText }}>
          No site is marked as the central function. A multi-site certificate needs one location from which the management system is directed — usually the head office.
        </div>
      )}

      {loading ? (
        <div style={{ color: theme.color.subtle, fontSize: 13 }}>Loading…</div>
      ) : (
        <div style={{ ...card, padding: 0, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 720 }}>
            <thead>
              <tr style={{ background: theme.color.panelAlt }}>
                <th style={th}>Site</th>
                <th style={th}>Country</th>
                <th style={th}>Address</th>
                <th style={th}>Scope</th>
                <th style={th}>In use</th>
                {isAdmin && <th style={{ ...th, width: 150 }} />}
              </tr>
            </thead>
            <tbody>
              {sites.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ ...cell, textAlign: "center", color: theme.color.subtle, padding: "32px 16px" }}>
                    No sites yet. {isAdmin ? "Add your head office first, then every other location the management system covers." : ""}
                  </td>
                </tr>
              )}
              {siteTree(sites).map((s) => (
                <React.Fragment key={s.id}>
                  <tr>
                    <td style={{ ...cell, paddingLeft: 16 + s.depth * 22 }}>
                      <PortalLink to={`/sites/${s.id}`} style={{ fontWeight: 600, color: theme.color.heading, textDecoration: "none" }} title="Open this site">
                        {s.depth > 0 && <span style={{ color: theme.color.faint, marginRight: 6 }}>└</span>}{s.name}
                      </PortalLink>
                      <div style={{ fontSize: 11, color: theme.color.subtle, marginTop: 2 }}>
                        {SITE_KIND_LABELS[s.kind]}{s.headcount != null ? ` · ${s.headcount} people` : ""}{s.parent ? ` · reports to ${s.parent.name}` : ""}
                      </div>
                      {s.legalEntityName && <div style={{ fontSize: 11, color: theme.color.subtle }}>{s.legalEntityName}</div>}
                      {s.departments.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
                          {s.departments.map((d) => (
                            <span key={d.id} style={{ fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 3, background: cbSafe(d.color + "22"), color: cbSafe(d.color) }}>{d.name}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td style={cell}>{s.country}</td>
                    <td style={{ ...cell, color: theme.color.muted, maxWidth: 260 }}>{address(s) || "—"}</td>
                    <td style={cell}>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {s.isCentralFunction && <span style={{ fontSize: 10, fontWeight: 600, color: theme.color.accentText, background: theme.color.accentBg, padding: "2px 6px", borderRadius: 2 }}>Central function</span>}
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 2, color: s.inScope ? theme.color.successText : theme.color.muted, background: s.inScope ? theme.color.successBg : theme.color.panelAlt }}>
                          {s.inScope ? "In scope" : "Out of scope"}
                        </span>
                        {!s.isLegalEstablishment && <span style={{ fontSize: 10, color: theme.color.muted, padding: "2px 6px", borderRadius: 2, background: theme.color.panelAlt }}>Not an establishment</span>}
                      </div>
                    </td>
                    <td style={{ ...cell, fontSize: 11, color: theme.color.muted, whiteSpace: "nowrap" }}>
                      <PortalLink to={`/sites/${s.id}`} style={{ color: "inherit", textDecoration: "none" }} title="Open this site">
                        {s.memberCount ?? 0} member{(s.memberCount ?? 0) === 1 ? "" : "s"}<br />
                        {s.controlCount ?? 0} control{(s.controlCount ?? 0) === 1 ? "" : "s"}<br />
                        {s.evidenceCount ?? 0} evidence · {s.riskCount ?? 0} risk{(s.riskCount ?? 0) === 1 ? "" : "s"}
                      </PortalLink>
                    </td>
                    {isAdmin && (
                      <td style={{ ...cell, textAlign: "right" }}>
                        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                          <PortalLink to={`/sites/${s.id}`} style={{ padding: "3px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.accentText, fontSize: 12, textDecoration: "none" }}>Open</PortalLink>
                          <button onClick={() => setEditing(s)} style={{ padding: "3px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.text, fontSize: 12, cursor: "pointer" }}>Edit</button>
                          <button onClick={() => { setConfirmDel(s.id); setDelError(""); }} style={{ padding: "3px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.danger, fontSize: 12, cursor: "pointer" }}>Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {confirmDel === s.id && (
                    <tr>
                      <td colSpan={colCount} style={{ ...cell, background: theme.color.panelAlt }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 13 }}>
                            Delete <strong>{s.name}</strong>? Member assignments and control tags go with it; evidence and risks stay but lose the site.
                            {" "}{s.country} stays in Operating Locations until you remove it on the organisation profile.
                          </span>
                          <button onClick={() => handleDelete(s.id)} disabled={deleting}
                            style={{ padding: "3px 12px", borderRadius: 3, border: "none", background: theme.color.danger, color: "#fff", fontSize: 12, fontWeight: 600, cursor: deleting ? "not-allowed" : "pointer", opacity: deleting ? 0.6 : 1 }}>
                            {deleting ? "Deleting…" : "Yes, delete"}
                          </button>
                          <button onClick={() => { setConfirmDel(null); setDelError(""); }}
                            style={{ padding: "3px 10px", borderRadius: 3, border: `1px solid ${theme.color.border}`, background: "transparent", color: theme.color.muted, fontSize: 12, cursor: "pointer" }}>
                            Cancel
                          </button>
                          {delError && <span style={{ fontSize: 12, color: theme.color.danger }}>{delError}</span>}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p style={{ fontSize: 12, color: theme.color.subtle, marginTop: 16, maxWidth: 640, lineHeight: 1.5 }}>
        A group whose subsidiaries hold their own certificates, regulators or data protection officers is set up as one
        organisation per subsidiary, linked as a group by your consultancy — not as sites.
      </p>

      {showCreate && <SiteModal hasCentralFunction={hasCentralFunction} sites={sites} onClose={() => setShowCreate(false)} onSaved={() => load()} />}
      {editing && <SiteModal initial={editing} hasCentralFunction={hasCentralFunction && !editing.isCentralFunction} sites={sites} onClose={() => setEditing(null)} onSaved={() => load()} />}
    </div>
  );
}
